import { FC, forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Switch } from '@progress/kendo-react-inputs';

import styles from './FeaturesSection.module.scss';
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle, StackLayout } from '@progress/kendo-react-layout';
import { Field, FieldArray, FieldArrayRenderProps, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { cancelIcon, pencilIcon, saveIcon } from '@progress/kendo-svg-icons';
import React from 'react';
import clone from 'lodash/cloneDeep';
import { ICustomerFeature, IFeatureParameterValue } from '../../../model/api/featuremanagement';
import { conditionalRequiredValidator, FormInput, regexpValidator, TextFormField, useConfirmation } from '../../common';
import { featuresService } from '../../../services/api/features';
import { notifications } from '../../../services';

interface IFeatureParameterEditorRef {
  onCancel: (parameters: IFeatureParameterValue[]) => void; // a method the parent can call on the ref
  onSave: (parameters: IFeatureParameterValue[]) => void; // a method the parent can call on the ref
}

// eslint-disable-next-line react/display-name
const FeatureParameterEditor = forwardRef<IFeatureParameterEditorRef, FieldArrayRenderProps>((fieldArrayRenderProps, ref) => {
  const { edit, parameters, reference } = fieldArrayRenderProps;

  useImperativeHandle(
    reference,
    () => {
      return {
        onCancel: (parameters: IFeatureParameterValue[]) => {
          if (parameters) {
            parameters.forEach((item: IFeatureParameterValue, index: number) => {
              fieldArrayRenderProps.onReplace({
                index: index,
                value: item,
              });
            });
          }
        },
        onSave: (parameters: IFeatureParameterValue[]) => {
          if (parameters) {
            parameters.forEach((item: IFeatureParameterValue, index: number) => {
              fieldArrayRenderProps.onReplace({
                index: index,
                value: item,
              });
            });
          }
        },
      };
    },
    [fieldArrayRenderProps],
  );

  return (
    <div>
      {parameters.map((item: IFeatureParameterValue, index: number) => (
        <Field
          key={index}
          name={`parameters[${index}].value`} // Path to the individual item's value
          label={
            <span>
              {item.parameter.key} <small>({item.parameter.description})</small>
            </span>
          }
          required={item.parameter.required}
          value={item.value}
          defValue={item.parameter.defaultValue}
          userEdited={item.userEdited}
          onUserChangedEvent={() => {
            item.userEdited = true;
          }}
          component={edit ? FormInput : TextFormField}
          arrayIndex={index}
          validator={(value) => regexpValidator(value, item.parameter.validationRegexp)}
        />
      ))}
    </div>
  );
});

const FeaturePanel: FC<{ customerFeature: ICustomerFeature; hasEditAccess: boolean }> = ({ customerFeature, hasEditAccess }) => {
  const [edit, setEdit] = useState<boolean>(false);
  const [feature, setFeature] = useState<ICustomerFeature>(clone(customerFeature));
  const [originalFeature, setOriginalFeature] = useState<ICustomerFeature>(clone(customerFeature));
  const [formKey, setFormKey] = useState(0); // Initial key
  const featureParameterEditorRef = useRef<IFeatureParameterEditorRef>(null);
  const confirmation = useConfirmation();
  const handleSubmit = useCallback(
    async (dataItem: { [name: string]: any }) => {
      const process = async () => {
        const saveFeature = clone(feature);
        saveFeature.parameters = dataItem.parameters;
        try {
          const savedFeature = await featuresService.save(saveFeature);
          const newFeature = clone(savedFeature);
          const originalFeature = clone(savedFeature);
          setFeature(newFeature);
          setOriginalFeature(originalFeature);
          featureParameterEditorRef.current?.onSave(newFeature.parameters);
          notifications.success(`Feature '${newFeature.feature.name}' saved successfully.`);
        } catch (e) {
          notifications.error(`Feature '${feature.feature.name}' saving failed.`);
        } finally {
          setEdit(false);
          setFormKey(formKey + 1);
        }
      };

      confirmation({
        message: `Are you sure you want to apply these changes?`,
      }).then(
        async () => await process(),
        () => {},
      );
    },
    [feature, confirmation, formKey],
  );

  const onCancelClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setFeature(clone(originalFeature));
      featureParameterEditorRef.current?.onCancel(feature.parameters);
      setEdit(false);
      setFormKey(formKey + 1);
    },
    [originalFeature, feature, formKey],
  );

  const handleFeatureEnabledChange = useCallback(
    (event: any) => {
      setFeature({ ...feature, enabled: event.value });
    },
    [feature],
  );

  const formValidator = (values: { enabled: boolean; parameters: { value: string }[] }) => {
    if (!values.enabled) {
      return {};
    }

    const errors: any = { parameters: [] };
    let hasErrors = false;

    values.parameters.forEach((param, index) => {
      const isParamRequired = customerFeature.parameters[index].parameter.required;

      const errorMessage = conditionalRequiredValidator(param.value, isParamRequired);

      if (errorMessage) {
        errors.parameters[index] = { value: errorMessage };
        hasErrors = true;
      } else {
        errors.parameters[index] = null;
      }
    });

    return hasErrors ? errors : {};
  };

  const content = useMemo(() => {
    return (
      <Form
        key={formKey}
        initialValues={{ parameters: feature.parameters, edit: edit, enabled: feature.enabled }}
        onSubmit={handleSubmit}
        validator={formValidator}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement className={styles.generalInfo} horizontal={false}>
            <Card className='k-flex-30'>
              <CardHeader className='k-hbox'>
                <div className={styles.featureHeader}>
                  <div className={styles.featureDescContent}>
                    <Field
                      name='enabled'
                      component={Switch}
                      label='Enable Feature'
                      checked={feature.enabled}
                      disabled={!edit}
                      onLabel={'On'}
                      offLabel={'Off'}
                      onChange={handleFeatureEnabledChange}
                    />
                    <CardTitle className={styles.title}>{feature.feature.name}</CardTitle>
                    <CardSubtitle className={styles.titleDesc}>{feature.feature.description}</CardSubtitle>
                  </div>
                  {hasEditAccess && (
                    <div style={{ display: 'flex', gap: 10 }}>
                      {!edit && (
                        <Button type='button' svgIcon={pencilIcon} onClick={() => setEdit(true)}>
                          Edit
                        </Button>
                      )}
                      {edit && (
                        <Button
                          type='button'
                          svgIcon={saveIcon}
                          disabled={!formRenderProps.allowSubmit || !formRenderProps.valid}
                          onClick={formRenderProps.onSubmit}
                        >
                          Save
                        </Button>
                      )}
                      {edit && (
                        <Button type='button' svgIcon={cancelIcon} onClick={onCancelClick}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                <StackLayout orientation='vertical'>
                  <FieldArray
                    name='parameters'
                    edit={edit}
                    value={feature.parameters}
                    parameters={feature.parameters}
                    reference={featureParameterEditorRef}
                    component={FeatureParameterEditor}
                  />
                </StackLayout>
              </CardBody>
            </Card>
          </FormElement>
        )}
      />
    );
  }, [feature, edit, handleSubmit, onCancelClick, handleFeatureEnabledChange]);

  return content;
};

export const FeaturesSection: FC<{ features: ICustomerFeature[]; hasEditAccess: boolean }> = ({ features, hasEditAccess }) => {
  return (
    <StackLayout orientation={'vertical'} gap={15} className={styles.verticalStack}>
      {features.map((feature) => (
        <FeaturePanel customerFeature={feature} hasEditAccess={hasEditAccess} key={feature.id} />
      ))}
    </StackLayout>
  );
};
